import { validateAndUpdateContactAddress } from '@/API/repositories/contact';
import { validatePhoneForCourier } from '@/common/functions/validatePhoneForCourier';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { useTranslationContext } from '@/common/hooks/useTranslationContext';
import { useRef, useState } from 'react';
import { REQUIRED_FIELDS_FOR_COUNTRY } from '../constants/countryAddressFields';
import { checkRequiredFieldsForCountry } from '../helpers/checkRequiredFieldsForCountry';

const ADDRESS_FORM_INPUTS = {
	city: '',
	street: '',
	quarter: '',
	streetNumber: '',
	building: '',
	additionalInfo: '',
	phoneForCourier: '',
	postalCode: '',
	apartment: '',
	entrance: '',
	staircase: '',
	floor: '',
	businessOrComplexName: '',
};

export const useAddressPopup = (contact, setContact) => {
	const [isAddressPopupOpen, setIsAddressPopupOpen] = useState(false);
	const [addressFormInputs, setAddressFormInputs] =
		useState(ADDRESS_FORM_INPUTS);
	const { addMessage } = useMessageQueueContext();
	const { makeRequest } = useRequestsContext();
	const [validationMessage, setValidationMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [validationCalls, setValidationCalls] = useState(0);
	const [isSaved, setIsSaved] = useState(false);
	const { translation } = useTranslationContext();

	const handleSubmitAddressForm = async (e, contactId, forceSave) => {
		e.preventDefault();
		setValidationMessage('');

		const isPhoneError =
			addressFormInputs.phoneForCourier &&
			!validatePhoneForCourier(addressFormInputs.phoneForCourier);

		const isInfoError =
			addressFormInputs.additionalInfo && addressFormInputs.additionalInfo > 30;

		if (isInfoError) {
			return addMessage(
				'The info for the courier must not exceed 30 characters.',
				'error',
			);
		}

		if (isPhoneError) {
			return addMessage(
				'Phone for the courier must consist of digits, spaces and hyphens',
				'error',
			);
		}

		let allRequiredFieldsFilled = checkRequiredFieldsForCountry(
			contact.country,
			addressFormInputs.city,
			addressFormInputs.postalCode,
			addressFormInputs.street,
			addressFormInputs.building,
		);

		if (!allRequiredFieldsFilled) {
			return addMessage('Please fill in all the required fields', 'error');
		}

		const payload = addressFormInputs;

		payload.forceSave = forceSave;

		setIsLoading(true);
		setValidationCalls((prev) => prev + 1);
		const response = await makeRequest(
			validateAndUpdateContactAddress.bind(null, contactId, payload),
		);
		const data = response.data;
		setIsLoading(false);

		setContact(data.contact);

		if (data.addressHasBeenSaved) {
			setIsSaved(true);

			setTimeout(() => {
				setIsSaved(false);
				setIsAddressPopupOpen(false);
				if (data.isStatusInvalid) {
					addMessage(data.messageTranslated || data.message, 'error');
				} else {
					addMessage(translation.saved, 'success');
				}
			}, 1500);
		} else {
			setValidationMessage(
				data.messageTranslated ? data.messageTranslated : data.message,
			);
		}
	};

	const handleChangeInputValue = (fieldName, event) => {
		const value = event.target.value;
		setAddressFormInputs((prev) => {
			return { ...prev, [fieldName]: value };
		});
	};

	const setAddressFormFromContactWhenAddressNotExists = (contact) => {
		setAddressFormInputs({
			...ADDRESS_FORM_INPUTS,
			postalCode: contact?.postal_code ?? '',
			city: contact?.city ?? '',
			phoneForCourier: contact?.phone_number_for_courier ?? '',
			street: contact?.street ?? '',
		});
	};

	const openContactAddressPopup = () => {
		setIsAddressPopupOpen(true);
		setValidationCalls(0);
		setAddressFormFromContact(contact);
	};

	const setAddressFormFromContact = (contact) => {
		setValidationMessage('');
		if (contact?.address_validation?.form_json) {
			const savedFormInputs = JSON.parse(contact.address_validation?.form_json);
			const formInputs = { ...ADDRESS_FORM_INPUTS, ...savedFormInputs };

			setAddressFormInputs(formInputs);
			if (
				contact?.address_validation?.validation_message &&
				contact?.address_validation?.validation_status === 'invalid'
			) {
				setValidationMessage(
					contact.address_validation.validation_message_translated ||
						contact.address_validation.validation_message,
				);
			}
			return;
		}
		setAddressFormFromContactWhenAddressNotExists(contact);
	};

	return {
		isAddressPopupOpen,
		setIsAddressPopupOpen,
		handleSubmitAddressForm,
		addressFormInputs,
		setAddressFormInputs,
		handleChangeInputValue,
		setAddressFormFromContact,
		validationMessage,
		setValidationMessage,
		openContactAddressPopup,
		isLoading,
		validationCalls,
		isSaved,
	};
};
